import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';

import {
  BulkFetchParameters,
  DEFAULT_MAX_RESULTS,
  GetAllIssuesByJQL,
  GetIssuePageByJQLParams,
  Issue,
  IssueSearchResponse,
  JiraField,
} from '@qality/common-model';
import { batchRequests } from '@qality/common-service';

import API from './API';

const splitIdsIntoChunks = (
  ids: Array<string | number>,
  chunkSize: number
): Array<Array<string | number>> => {
  const inputArray = uniq(ids);
  return inputArray.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / chunkSize);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);
};

type SearchIssuesResponse = {
  issues: Array<Issue>;
  total: number;
};

export default {
  async bulkFetchIssuesByIds({
    ids,
  }: BulkFetchParameters): Promise<Array<Issue>> {
    const chunkedIssueIds = splitIdsIntoChunks(ids, 50);

    return await Promise.all(
      chunkedIssueIds.map((idsChunk) => this.searchIssuesById(idsChunk))
    ).then((serviceResponses) => {
      const mappedIssuesFromServiceArray = serviceResponses.map(
        (serviceResponse) => serviceResponse.issues
      );
      return mappedIssuesFromServiceArray.flat();
    });
  },

  async getIssuePageByJQL({
    jql,
    fields = [],
    startAt = 0,
    maxResults = DEFAULT_MAX_RESULTS,
    retry = true,
  }: GetIssuePageByJQLParams): Promise<IssueSearchResponse> {
    const requestArgs = {
      url: '/rest/api/2/search',
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        jql,
        maxResults,
        fields,
        startAt,
      }),
    };

    const result = await API[retry ? 'request' : 'noRetry'](requestArgs).then(
      (response) => {
        return JSON.parse(response.body);
      }
    );

    return {
      issues: result.issues,
      total: result.total,
    };
  },

  async searchIssuesById(idsArray: Array<string | number>) {
    if (!idsArray.length) {
      return {};
    }

    const url = `/rest/api/2/search?jql=id in (${idsArray
      .filter(Boolean)
      .join(',')})&validateQuery=false`;
    const response = await API.request(url);

    return JSON.parse(response.body);
  },

  async searchApi(
    jql: string,
    startAt: number,
    size: number,
    fields: Array<JiraField>
  ) {
    const params = {
      url: `/rest/api/2/search`,
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        startAt,
        maxResults: size,
        jql,
        ...(!isNil(fields) && fields.length > 0 ? { fields } : {}),
      }),
      validateQuery: false,
    };
    return API.request(params).then((response) => {
      return JSON.parse(response.body);
    });
  },

  async getAllIssuesByJql(params: GetAllIssuesByJQL): Promise<Array<Issue>> {
    try {
      const MAX_ASYNC_REQUESTS = 10;
      const MAX_ISSUES_PER_REQUEST = 100;
      const STARTING_INDEX = 0;

      const { total, issues: firstRequestIssues } = await this.searchApi(
        params.jql,
        STARTING_INDEX,
        MAX_ISSUES_PER_REQUEST,
        params.fields
      );

      let startAt = STARTING_INDEX + MAX_ISSUES_PER_REQUEST;
      const searchRequestsArray = [];

      do {
        const currentStartAt = startAt;
        searchRequestsArray.push(() =>
          this.searchApi(
            params.jql,
            currentStartAt,
            MAX_ISSUES_PER_REQUEST,
            params.fields
          )
        );
        startAt += MAX_ISSUES_PER_REQUEST;
      } while (startAt < total);

      const result: Array<SearchIssuesResponse> =
        await batchRequests<SearchIssuesResponse>(
          searchRequestsArray,
          MAX_ASYNC_REQUESTS
        );

      const allIssues: Array<Issue> = [];
      allIssues.push(...firstRequestIssues);
      allIssues.push(...result.flatMap((response) => response.issues));

      return allIssues;
    } catch (e) {
      return [];
    }
  },
};
